import { Button, Form } from "antd";
import BaseModalForm from "components/BaseModalForm";
import message from "components/CustomMessage";
import { MODAL_SIZE_MEDIUM } from "constants/Layout";
import { PermissionSetMap } from "constants/RBACConstants";
import { useActiveUserContext } from "contexts/ActiveUserContext";
import { useOAuthEnabledTools } from "hooks/useOAuthEnabledTools";
import _ from "lodash";
import React, { forwardRef, useCallback, useMemo, useState } from "react";
import ToolsIntegrationsAPI from "restAPI/ToolsIntegrationAPI";
import { hasPermission } from "utils/authService";
import {
  FieldTypes,
  emailValidator,
  nameRegexValidator,
  urlRegexValidator,
} from "utils/formUtils";
import ModalUtil from "utils/modalUtil";
import { duplicateNameValidator } from "utils/validatorUtil";

import AsanaConfigMappingForm from "./AsanaConfigMappingForm";
import AsanaOAuthPopup from "./AsanaOAuthPopup";

type AsanaConfigLoginFormProps = {
  name: string;
  loginConfigList: any[];
  handleMapping: (response: any) => void;
  config: any;
  onUpdateConfigComponent: (shouldUpdate?: boolean) => void;
  afterClose: () => void;
  displayName: string;
};

function AsanaConfigLoginForm(
  {
    name,
    loginConfigList,
    config,
    onUpdateConfigComponent,
    ...props
  }: AsanaConfigLoginFormProps,
  ref: any,
) {
  const [authSelection, setAuthSelection] = useState(() =>
    config?.tokenType
      ? config.tokenType === "PERSONAL_ACCESS_TOKEN"
        ? "default"
        : "oauth"
      : "default",
  );
  const isEdit = !_.isUndefined(config);
  const [form] = Form.useForm();
  const { sessionData } = useActiveUserContext();
  const OAuthEnabledTools = useOAuthEnabledTools();
  const showOAuthOption =
    OAuthEnabledTools?.includes("ASANA") ||
    !sessionData?.isCustomerDeployedInstance;
  const onOk = async (data: any) => {
    await form.validateFields();
    try {
      const payload = {
        ...data,
        tokenType: "PERSONAL_ACCESS_TOKEN",
      };
      const response = await ToolsIntegrationsAPI[
        !isEdit
          ? "postTicketSystemToolLoginDetails"
          : "updateTicketSystemToolLoginDetails"
      ]("ASANA", payload, config?.id).promise;

      message.success(`${name} Login Configuration created`);
      if (!isEdit) {
        ModalUtil.show({
          content: (
            <AsanaConfigMappingForm
              onUpdateConfigComponent={onUpdateConfigComponent}
              name={name}
              loginConfigId={response.id}
            />
          ),
        });
      }
      onUpdateConfigComponent?.(true);
    } catch (err: any) {
      console.log(err);
      if (!_.isEmpty(err.response.data.errors)) {
        const { field, defaultMessage } = err.response.data.errors[0];
        form.setFields([
          {
            name: field,
            errors: [defaultMessage],
          },
        ]);
      }
      return Promise.reject(err.response?.data?.message);
    }
  };

  const modalProps = {
    title: <>{isEdit ? "Edit" : "Add"} Asana Login Credentials </>,
    width: MODAL_SIZE_MEDIUM,
    disabled: !hasPermission(
      isEdit ? PermissionSetMap.EDIT_TOOL_CONFIG : PermissionSetMap.WRITE_TOOLS,
    ),
  };

  const showOAuthPopup = useCallback(() => {
    ModalUtil.show({
      content: <AsanaOAuthPopup {...config} />,
      afterClose: (failed, loginId) => {
        if (!failed) {
          onUpdateConfigComponent?.(true);
          message.success(`${name} OAuth Login Configuration created`);
          ModalUtil.show({
            content: (
              <AsanaConfigMappingForm
                onUpdateConfigComponent={onUpdateConfigComponent}
                name={name}
                loginConfigId={config?.id ?? loginId}
              />
            ),
          });
        }
        ref?.current?.close();
      },
    });
  }, [name, config, onUpdateConfigComponent, ref]);

  const fields = useMemo(() => {
    return [
      {
        type: FieldTypes.RADIO,
        options: [
          { label: "Personal Access Token", value: "default" },
          ...(showOAuthOption ? [{ label: "OAuth", value: "oauth" }] : []),
        ],
        props: {
          onChange: (e: any) => setAuthSelection(e.target.value),
        },
        name: "authSelection",
      },
      ...(authSelection === "default"
        ? [
            {
              name: "name",
              label: "Name",
              colSpan: 12,
              props: {
                autoFocus: true,
              },
              required: true,
              rules: [
                duplicateNameValidator({ list: loginConfigList }),
                nameRegexValidator,
              ],
            },
            {
              name: "url",
              label: "URL",
              colSpan: 12,
              rules: [urlRegexValidator],
              required: true,
            },
            {
              name: "userName",
              label: "Email",
              rules: [emailValidator],
              colSpan: 12,
              required: true,
            },
            {
              name: "apiToken",
              label: "API Token",
              type: FieldTypes.PASSWORD,
              colSpan: 12,
              required: true,
            },
            {
              name: "version",
              label: "Version",
              colSpan: 12,
              disabled: true,
            },
          ]
        : [
            {
              type: "Label",
              render: () => (
                <Button type="primary" onClick={showOAuthPopup}>
                  Login with OAuth
                </Button>
              ),
            },
          ]),
    ];
  }, [authSelection, loginConfigList, showOAuthPopup, showOAuthOption]);

  const ModalFormComponent = BaseModalForm;

  return (
    <ModalFormComponent
      modalProps={modalProps}
      ref={ref}
      afterClose={props.afterClose}
      initialValues={{ ...config, version: "V3", authSelection }}
      form={form}
      fields={fields}
      onOk={onOk}
      title={modalProps.title}
    />
  );
}

export default forwardRef(AsanaConfigLoginForm);
